import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import LemonSqueezyBuyButton from './LemonSqueezyBuyButton';
import PatternTags from './PatternTags';
import { ChevronLeft, Info, Ruler, Scissors } from 'lucide-react';
import { getDifficultyLevel } from '../../constants/patternOptions';
import { getPatternNeedles, getPatternYarnWeights } from '../../utils/patternFields';

export default function PatternPageContent({ pattern, isPreview = false }) {
    const [selectedImage, setSelectedImage] = useState(pattern.main_image || pattern.images?.[0] || '');

    useEffect(() => {
        setSelectedImage(pattern.main_image || pattern.images?.[0] || '');
    }, [pattern]);

    const difficulty = getDifficultyLevel(pattern.difficulty_level || pattern.skill_level);
    const yarnWeights = getPatternYarnWeights(pattern);
    const needles = getPatternNeedles(pattern);

    return (
        <div className="bg-white min-h-screen pb-20">
            {isPreview && (
                <div className="border-b border-amber-200 bg-amber-50 px-4 py-3 text-center text-sm text-amber-900" role="status">
                    <strong>Draft preview.</strong> This pattern is not visible to customers and changes have not been published.
                </div>
            )}
            <div className="container mx-auto px-4 py-8">
                <Link to="/shop" className="inline-flex items-center gap-2 text-gray-500 hover:text-primary transition-colors mb-8 group">
                    <ChevronLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
                    Back to shop
                </Link>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                    <div className="space-y-4">
                        <div className="aspect-[4/5] rounded-3xl overflow-hidden border border-gray-100 bg-gray-50">
                            <img
                                src={selectedImage || '/images/pattern-placeholder.jpg'}
                                alt={pattern.title || 'Pattern preview'}
                                className="w-full h-full object-cover"
                            />
                        </div>
                        {pattern.images && pattern.images.length > 1 && (
                            <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
                                {pattern.images.map((img, idx) => (
                                    <button
                                        type="button"
                                        key={img || idx}
                                        onClick={() => setSelectedImage(img)}
                                        className={`flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden border-2 transition-all ${selectedImage === img ? 'border-primary ring-2 ring-primary/10' : 'border-transparent opacity-70 hover:opacity-100'}`}
                                    >
                                        <img src={img} className="w-full h-full object-cover" alt={`Preview ${idx + 1}`} />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="flex flex-col">
                        <div className="mb-6">
                            <PatternTags
                                categories={pattern.categories}
                                yarnWeights={yarnWeights}
                                size={pattern.size_tag}
                            />
                            <h1 className="text-4xl lg:text-5xl font-primary mt-4 mb-2">{pattern.title || 'Untitled pattern'}</h1>
                            <div className="flex items-center gap-4">
                                <span className="text-3xl font-bold text-gray-900">{pattern.price || 0}€</span>
                                {pattern.previous_price && (
                                    <span className="text-xl text-gray-400 line-through">{pattern.previous_price}€</span>
                                )}
                            </div>
                        </div>

                        <div className="space-y-8">
                            <LemonSqueezyBuyButton
                                pattern={pattern}
                                isPreview={isPreview}
                                className="py-4 text-lg rounded-2xl shadow-lg shadow-primary/20"
                            />

                            <div className="prose prose-stone max-w-none">
                                <h3 className="text-lg font-semibold flex items-center gap-2 mb-3">
                                    <Info size={18} className="text-primary" />
                                    About this pattern
                                </h3>
                                <p className="text-gray-600 leading-relaxed whitespace-pre-wrap">
                                    {pattern.description}
                                </p>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                <div className="bg-gray-50 rounded-2xl p-4">
                                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                                        <Scissors size={14} /> Materials
                                    </h4>
                                    <div className="space-y-2">
                                        {pattern.materials?.yarn && (
                                            <p className="text-sm"><strong>Yarn:</strong> {pattern.materials.yarn}</p>
                                        )}
                                        {needles.length > 0 && (
                                            <div className="text-sm">
                                                <strong>Needles:</strong>
                                                <ul className="mt-1 list-disc list-inside">
                                                    {needles.map(needle => <li key={needle}>{needle}</li>)}
                                                </ul>
                                            </div>
                                        )}
                                        {pattern.materials?.other?.length > 0 && (
                                            <ul className="text-sm list-disc list-inside">
                                                {pattern.materials.other.map((material, index) => (
                                                    <li key={`${material}-${index}`}>{material}</li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>

                                <div className="bg-gray-50 rounded-2xl p-4">
                                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                                        <Ruler size={14} /> Technical Details
                                    </h4>
                                    <div className="space-y-2 text-sm">
                                        <div>
                                            <p><strong>Level:</strong> {'★'.repeat(difficulty.stars)} {difficulty.shortLabel}</p>
                                            <p className="mt-1 text-xs leading-relaxed text-gray-500">{difficulty.description}</p>
                                        </div>
                                        {pattern.construction && <p><strong>Construction:</strong> {pattern.construction}</p>}
                                        <p><strong>Yarn Weights:</strong> {yarnWeights.join(', ')}</p>
                                        {pattern.pdf_version && <p><strong>PDF Version:</strong> {pattern.pdf_version}</p>}
                                        {pattern.pdf_date && <p><strong>PDF Date:</strong> {pattern.pdf_date}</p>}
                                        <p><strong>Languages:</strong> {pattern.languages?.join(', ')}</p>
                                    </div>
                                </div>
                            </div>

                            {pattern.changelog?.length > 0 && (
                                <div className="rounded-2xl border border-gray-100 p-5">
                                    <h3 className="text-sm font-semibold">Pattern updates</h3>
                                    <div className="mt-3 space-y-3">
                                        {pattern.changelog.map((entry, index) => (
                                            <div key={`${entry.version}-${entry.date}-${index}`} className="text-sm">
                                                <p className="font-medium">Version {entry.version || '—'} {entry.date ? `· ${entry.date}` : ''}</p>
                                                {entry.notes && <p className="mt-1 text-gray-600">{entry.notes}</p>}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
